// MFA Setup page
// Users can enable or disable TOTP-based two-factor authentication
// Accessible from the avatar dropdown menu

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar";

const API_URL = config.API_URL;

export default function MFASetup() {
  const navigate = useNavigate();
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const token = sessionStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [step, setStep] = useState("idle"); // idle, setup, verify, success
  const [qrUrl, setQrUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  useEffect(() => {
    // Check current MFA status from the user's auth/me endpoint
    axios.get(`${API_URL}/auth/me`, authHeaders)
      .then(res => setMfaEnabled(res.data.mfa_enabled === "true"))
      .catch(() => {});
  }, []);

  async function handleStartSetup() {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/auth/mfa/setup`, {}, authHeaders);
      setQrUrl(res.data.qr_url);
      setSecret(res.data.secret);
      setStep("setup");
    } catch (err) {
      setError("Failed to start MFA setup. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/auth/mfa/verify-setup?code=${encodeURIComponent(code)}`,
        {},
        authHeaders
      );
      setStep("success");
      setMfaEnabled(true);
    } catch (err) {
      setError("Invalid code. Please try again.");
      setCode("");
    } finally {
      setLoading(false);
    }
  }

  async function handleDisable() {
    if (!window.confirm("Are you sure you want to disable MFA? This will make your account less secure.")) return;
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/auth/mfa/disable`, {}, authHeaders);
      setMfaEnabled(false);
      setStep("idle");
    } catch (err) {
      setError("Failed to disable MFA. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Generate QR code image URL using Google Charts API
  const qrImageUrl = qrUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`
    : "";

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar />

      <main className="px-8 py-8 max-w-2xl mx-auto">

        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Two-Factor Authentication</h1>
          <p className="text-gray-400 mt-1">
            Add an extra layer of security to your account using an authenticator app.
          </p>
        </div>

        {/* Current status */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${mfaEnabled ? "bg-green-500" : "bg-gray-500"}`}></div>
              <div>
                <p className="text-white font-medium">
                  {mfaEnabled ? "Two-Factor Authentication is enabled" : "Two-Factor Authentication is disabled"}
                </p>
                <p className="text-gray-400 text-sm mt-0.5">
                  {mfaEnabled
                    ? "Your account is protected with TOTP authentication."
                    : "Enable 2FA to protect your account with Google Authenticator, Microsoft Authenticator, or Authy."}
                </p>
              </div>
            </div>
            {mfaEnabled ? (
              <button
                onClick={handleDisable}
                disabled={loading}
                className="bg-red-800 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                Disable MFA
              </button>
            ) : (
              step === "idle" && (
                <button
                  onClick={handleStartSetup}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-500 text-white text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? "Setting up..." : "Enable MFA"}
                </button>
              )
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Step 1 — Show QR code */}
        {step === "setup" && (
          <div className="bg-gray-900 rounded-xl p-6 border border-blue-800">
            <h2 className="text-white font-semibold mb-4">Step 1 — Scan QR Code</h2>
            <p className="text-gray-400 text-sm mb-6">
              Open your authenticator app (Google Authenticator, Microsoft Authenticator, or Authy) and scan this QR code.
            </p>

            {/* QR Code */}
            <div className="flex justify-center mb-6">
              <div className="bg-white p-4 rounded-xl">
                <img src={qrImageUrl} alt="MFA QR Code" width={200} height={200} />
              </div>
            </div>

            {/* Manual entry fallback */}
            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <p className="text-gray-400 text-xs mb-2">Can't scan? Enter this code manually:</p>
              <p className="text-white font-mono text-sm tracking-widest">{secret}</p>
            </div>

            <button
              onClick={() => setStep("verify")}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              I've scanned the QR code →
            </button>
          </div>
        )}

        {/* Step 2 — Verify code */}
        {step === "verify" && (
          <div className="bg-gray-900 rounded-xl p-6 border border-blue-800">
            <h2 className="text-white font-semibold mb-4">Step 2 — Verify Code</h2>
            <p className="text-gray-400 text-sm mb-6">
              Enter the 6-digit code from your authenticator app to confirm setup.
            </p>
            <form onSubmit={handleVerify}>
              <div className="mb-6">
                <label className="block text-gray-400 text-sm mb-2">Authentication code</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors text-center text-2xl tracking-widest font-mono"
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Verifying..." : "Enable MFA"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("setup"); setCode(""); setError(null); }}
                className="w-full mt-3 text-gray-400 hover:text-white text-sm transition-colors"
              >
                ← Back to QR code
              </button>
            </form>
          </div>
        )}

        {/* Success */}
        {step === "success" && (
          <div className="bg-gray-900 rounded-xl p-6 border border-green-800 text-center">
            <div className="text-4xl mb-4">✅</div>
            <h2 className="text-white font-semibold text-lg mb-2">MFA Enabled Successfully</h2>
            <p className="text-gray-400 text-sm mb-6">
              Your account is now protected with two-factor authentication. You will be asked for a code each time you log in.
            </p>
            <button
              onClick={() => navigate("/")}
              className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        )}

      </main>
    </div>
  );
}